// shell/components/virtual-scroller/virtual-scroller.ts

import { IDisposable, IndexRange } from '../types';
import { DisposableStore } from '../disposable';

/**
 * Configuration for the VirtualScroller component.
 */
export interface VirtualScrollerConfig {
    container: HTMLElement;
    estimatedRowHeight: number;
    bufferRows: number;
    totalCount: number;

    /**
     * Render callback - renderer creates elements in the provided container.
     * Renderer MUST call observeRenderedElements() after creating row elements.
     */
    onRender: (container: HTMLElement, startIndex: number, endIndex: number) => void;

    /**
     * Optional: render loading placeholders for items being fetched.
     */
    onRenderLoading?: (container: HTMLElement, startIndex: number, endIndex: number) => void;

    /**
     * Called when visible range changes. Use for data prefetching.
     */
    onRangeChange?: (startIndex: number, endIndex: number) => void;

    /**
     * For grid layouts: return number of columns.
     * Called on resize to detect layout changes.
     */
    getColumnsPerRow?: () => number;

    /**
     * Gap between rows in pixels.
     */
    rowGap?: number;

    /**
     * Accessibility configuration.
     */
    role?: 'listbox' | 'grid' | 'table';
    ariaLabel?: string;
    announcePosition?: (position: number, total: number) => string;

    /**
     * Sticky header configuration.
     */
    stickyHeader?: {
        element: HTMLElement;
        height: number;
    };
}

/**
 * VirtualScroller provides efficient rendering of large lists through windowed virtualization.
 *
 * It renders only visible items plus a buffer zone, uses binary search for O(log n) range
 * finding, tracks actual row heights via ResizeObserver, and preserves scroll position
 * when heights change.
 *
 * Ownership boundaries:
 * - VirtualScroller owns: scroll math, range calculation, positioning
 * - Renderers own: DOM element creation, styling, event binding
 */
export class VirtualScroller implements IDisposable {
    private readonly config: VirtualScrollerConfig;
    private readonly disposables = new DisposableStore();

    // DOM elements
    private readonly container: HTMLElement;
    private readonly scrollHost: HTMLElement;
    private readonly scrollablePart: HTMLElement;
    private readonly renderedBlock: HTMLElement;
    private readonly liveRegion: HTMLElement;
    private readonly stickyHeaderContainer: HTMLElement | null = null;

    // Configuration
    private totalCount: number;
    private estimatedRowHeight: number;
    private bufferRows: number;
    private rowGap: number;

    // Height tracking (keyed by visual row index, not item index)
    private rowHeights = new Map<number, number>();
    private cumulativeHeights: number[] = [];
    private cumulativeHeightsDirty = true;

    // Column tracking for grids
    private columnsPerRow = 1;
    private previousColumnsPerRow = 1;

    // Render state
    private renderedRange: IndexRange | null = null;
    private renderedBlockTop = 0;
    private renderScheduled = false;

    // Focus state
    private focusedIndex = 0;

    // Loading state tracking (efficient interval-based)
    private loadingIntervals: IndexRange[] = [];

    // ResizeObserver tracking (incremental observation)
    private itemResizeObserver!: ResizeObserver;  // Initialized in setupResizeObserver() called from constructor
    private observedElements = new WeakSet<HTMLElement>();
    private currentElements = new Set<HTMLElement>();

    // Height change batching
    private pendingHeightChanges = new Map<number, { oldHeight: number; newHeight: number }>();
    private flushHeightChangesScheduled = false;

    // Screen reader announcement debouncing
    private announcementScheduled = false;

    constructor(config: VirtualScrollerConfig) {
        this.config = config;
        this.container = config.container;
        this.totalCount = config.totalCount;
        this.estimatedRowHeight = config.estimatedRowHeight;
        this.bufferRows = config.bufferRows;
        this.rowGap = config.rowGap ?? 0;

        // Initialize column count
        if (config.getColumnsPerRow) {
            this.columnsPerRow = config.getColumnsPerRow();
            this.previousColumnsPerRow = this.columnsPerRow;
        }

        // Build DOM structure
        this.container.classList.add('cs-virtual-scroller');
        if (config.role) {
            this.container.setAttribute('role', config.role);
        }
        if (config.ariaLabel) {
            this.container.setAttribute('aria-label', config.ariaLabel);
        }
        this.container.setAttribute('aria-rowcount', String(this.totalCount));

        // Sticky header container (optional)
        if (config.stickyHeader) {
            this.stickyHeaderContainer = document.createElement('div');
            this.stickyHeaderContainer.className = 'cs-virtual-scroller__sticky-header';
            this.stickyHeaderContainer.appendChild(config.stickyHeader.element);
            this.container.appendChild(this.stickyHeaderContainer);
        }

        // Scroll host
        this.scrollHost = document.createElement('div');
        this.scrollHost.className = 'cs-virtual-scroller__scroll-host';
        this.container.appendChild(this.scrollHost);

        // Scrollable container (sets total height for scrollbar)
        this.scrollablePart = document.createElement('div');
        this.scrollablePart.className = 'cs-virtual-scroller__scrollable';
        this.scrollHost.appendChild(this.scrollablePart);

        // Rendered block (positioned via transform)
        this.renderedBlock = document.createElement('div');
        this.renderedBlock.className = 'cs-virtual-scroller__rendered-block';
        this.scrollablePart.appendChild(this.renderedBlock);

        // Live region for screen reader announcements
        this.liveRegion = document.createElement('div');
        this.liveRegion.className = 'cs-virtual-scroller__live-region';
        this.liveRegion.setAttribute('aria-live', 'polite');
        this.liveRegion.setAttribute('aria-atomic', 'true');
        this.container.appendChild(this.liveRegion);

        // Setup observers and handlers
        this.setupResizeObserver();
        this.setupScrollHandler();
        this.setupContainerResizeObserver();

        // Initial render
        this.scheduleRender();
    }

    // ===== Count Management =====

    /**
     * Update the total item count.
     */
    public setTotalCount(count: number): void {
        if (count === this.totalCount) return;
        this.totalCount = count;
        this.container.setAttribute('aria-rowcount', String(count));
        this.cumulativeHeightsDirty = true;
        this.scheduleRender();
    }

    /**
     * Get the current total item count.
     */
    public getTotalCount(): number {
        return this.totalCount;
    }

    // ===== Height Tracking =====

    /**
     * Update the measured height of a visual row.
     * Called by the renderer after measuring rendered elements.
     */
    public updateRowHeight(visualRow: number, height: number): void {
        const oldHeight = this.rowHeights.get(visualRow);

        if (oldHeight !== height) {
            this.rowHeights.set(visualRow, height);
            this.cumulativeHeightsDirty = true;
        }
    }

    /**
     * Update the row gap between rows.
     * Triggers recalculation of cumulative heights.
     */
    public setRowGap(gap: number): void {
        if (this.rowGap !== gap) {
            this.rowGap = gap;
            this.cumulativeHeightsDirty = true;
            this.scheduleRender();
        }
    }

    /**
     * Update the estimated row height for rows not yet measured.
     * Triggers recalculation of cumulative heights.
     */
    public setEstimatedRowHeight(height: number): void {
        if (this.estimatedRowHeight !== height) {
            this.estimatedRowHeight = height;
            this.cumulativeHeightsDirty = true;
            this.scheduleRender();
        }
    }

    /**
     * Convert item index to visual row index.
     */
    public getVisualRowForItem(itemIndex: number): number {
        return Math.floor(itemIndex / this.columnsPerRow);
    }

    /**
     * Get item indices for a visual row.
     */
    public getItemsForVisualRow(visualRow: number): IndexRange {
        const start = visualRow * this.columnsPerRow;
        const end = Math.min(start + this.columnsPerRow - 1, this.totalCount - 1);
        return { start, end };
    }

    // ===== Scroll Control =====

    /**
     * Scroll to bring an item into view.
     */
    public scrollToIndex(
        index: number,
        align: 'start' | 'center' | 'end' = 'center',
        smooth = false
    ): void {
        const row = Math.floor(index / this.columnsPerRow);

        this.rebuildCumulativeHeights();
        const rowTop = this.cumulativeHeights[row] || 0;
        const rowHeight = this.rowHeights.get(row) ?? this.estimatedRowHeight;
        const usingEstimate = !this.rowHeights.has(row);

        let scrollTarget: number;
        const clientHeight = this.scrollHost.clientHeight;

        switch (align) {
            case 'start':
                scrollTarget = rowTop;
                break;
            case 'end':
                scrollTarget = rowTop + rowHeight - clientHeight;
                break;
            case 'center':
            default:
                scrollTarget = rowTop + rowHeight / 2 - clientHeight / 2;
                break;
        }

        scrollTarget = Math.max(0, Math.min(scrollTarget, this.getTotalEstimatedHeight() - clientHeight));

        if (smooth) {
            this.scrollHost.scrollTo({ top: scrollTarget, behavior: 'smooth' });
        } else {
            this.scrollHost.scrollTop = scrollTarget;
        }

        // If using estimated height, re-scroll after measurement
        if (usingEstimate) {
            requestAnimationFrame(() => {
                const actualHeight = this.rowHeights.get(row);
                if (actualHeight !== undefined && actualHeight !== this.estimatedRowHeight) {
                    this.scrollToIndex(index, align, false);
                }
            });
        }
    }

    /**
     * Get current scroll position.
     */
    public getScrollTop(): number {
        return this.scrollHost.scrollTop;
    }

    /**
     * Set scroll position.
     */
    /**
     * Synchronously update the scrollable area height based on current totalCount.
     * Call before setScrollTop() when totalCount was recently changed and the
     * deferred render hasn't run yet — otherwise the browser clamps scrollTop to 0.
     */
    public syncScrollableHeight(): void {
        const estimatedHeight = this.getTotalEstimatedHeight();
        this.scrollablePart.style.height = `${estimatedHeight}px`;
    }

    public setScrollTop(value: number): void {
        this.scrollHost.scrollTop = value;
    }

    // ===== Focus Management =====

    /**
     * Set the focused item index.
     */
    public setFocusedIndex(index: number): void {
        if (index < 0 || index >= this.totalCount) return;

        this.focusedIndex = index;

        // Ensure focused item is in rendered range
        if (!this.isInRenderedRange(index)) {
            this.scrollToIndex(index, 'center');
        }

        // Update tabindex (roving tabindex pattern)
        this.updateRovingTabindex();

        // Announce to screen readers (debounced)
        this.scheduleAnnouncement();
    }

    /**
     * Get the currently focused item index.
     */
    public getFocusedIndex(): number {
        return this.focusedIndex;
    }

    // ===== Rendered Range =====

    /**
     * Get the currently rendered item range.
     */
    public getRenderedRange(): IndexRange | null {
        return this.renderedRange;
    }

    // ===== Item Count Changes =====

    /**
     * Handle item count changes that affect scroll position.
     * Call this after adding/removing items.
     */
    public handleItemCountChange(
        changeIndex: number,
        countDelta: number,
        heightDelta: number
    ): void {
        const changedRow = Math.floor(changeIndex / this.columnsPerRow);
        const firstVisibleRow = this.findRowAtPosition(this.scrollHost.scrollTop);

        if (changedRow < firstVisibleRow) {
            // Items added/removed above viewport - adjust scroll
            this.scrollHost.scrollTop += heightDelta;
        }

        // Row heights need to be shifted/invalidated
        if (countDelta !== 0) {
            this.shiftRowHeights(changedRow, countDelta > 0 ? 1 : -1);
        }

        this.cumulativeHeightsDirty = true;
        this.scheduleRender();
    }

    // ===== Loading State =====

    /**
     * Mark a range as loading.
     */
    public setLoadingRange(startIndex: number, endIndex: number): void {
        this.loadingIntervals = this.mergeInterval(
            this.loadingIntervals,
            { start: startIndex, end: endIndex }
        );
        this.scheduleRender();
    }

    /**
     * Clear loading state for a range.
     */
    public clearLoadingRange(startIndex: number, endIndex: number): void {
        this.loadingIntervals = this.removeInterval(
            this.loadingIntervals,
            { start: startIndex, end: endIndex }
        );
        this.scheduleRender();
    }

    /**
     * Check if an index is in a loading range.
     * Uses binary search for O(log n) lookup.
     */
    public isIndexLoading(index: number): boolean {
        let low = 0;
        let high = this.loadingIntervals.length - 1;

        while (low <= high) {
            const mid = Math.floor((low + high) / 2);
            const interval = this.loadingIntervals[mid];

            if (index < interval.start) {
                high = mid - 1;
            } else if (index > interval.end) {
                low = mid + 1;
            } else {
                return true;
            }
        }

        return false;
    }

    // ===== ResizeObserver Management =====

    /**
     * Observe elements for height changes.
     * Renderer MUST call this after creating row elements.
     * Each element must have data-virtual-row attribute set to its visual row index.
     */
    public observeRenderedElements(elements: HTMLElement[]): void {
        const newElements = new Set(elements);

        // Unobserve elements that are no longer rendered
        for (const el of this.currentElements) {
            if (!newElements.has(el)) {
                this.itemResizeObserver.unobserve(el);
            }
        }

        // Observe new elements
        for (const element of elements) {
            if (element.dataset.virtualRow !== undefined && !this.observedElements.has(element)) {
                this.itemResizeObserver.observe(element);
                this.observedElements.add(element);
            }
        }

        this.currentElements = newElements;
    }

    /**
     * Stop observing a specific element.
     */
    public unobserveElement(element: HTMLElement): void {
        this.itemResizeObserver.unobserve(element);
        this.currentElements.delete(element);
    }

    // ===== Lifecycle =====

    /**
     * Force a re-render (clears cached range, triggers full rebuild).
     */
    public refresh(): void {
        this.renderedRange = null;
        this.cumulativeHeightsDirty = true;
        this.scheduleRender();
    }

    /**
     * Schedule a re-render that forces the render callback to fire.
     * Use this for updates that need to refresh visual state (like selection)
     * without recalculating cumulative heights.
     */
    public scheduleUpdate(): void {
        this.renderedRange = null;
        this.scheduleRender();
    }

    /**
     * Clean up all resources.
     */
    public dispose(): void {
        // Cancel any pending operations
        this.renderScheduled = false;
        this.flushHeightChangesScheduled = false;
        this.announcementScheduled = false;

        this.disposables.dispose();
        this.rowHeights.clear();
        this.cumulativeHeights = [];
        this.pendingHeightChanges.clear();
        this.loadingIntervals = [];
        this.currentElements.clear();
    }

    // ===== Private: Binary Search =====

    private rebuildCumulativeHeights(): void {
        if (!this.cumulativeHeightsDirty) return;

        const numRows = Math.ceil(this.totalCount / this.columnsPerRow);

        // Reuse array if same size to reduce GC pressure
        if (this.cumulativeHeights.length !== numRows + 1) {
            this.cumulativeHeights = new Array(numRows + 1);
        }
        this.cumulativeHeights[0] = 0;

        let cumulative = 0;
        for (let row = 0; row < numRows; row++) {
            const rowHeight = this.rowHeights.get(row) ?? this.estimatedRowHeight;
            cumulative += rowHeight + this.rowGap;
            this.cumulativeHeights[row + 1] = cumulative;
        }

        this.cumulativeHeightsDirty = false;
    }

    private findRowAtPosition(scrollY: number): number {
        this.rebuildCumulativeHeights();

        // Binary search for the row containing scrollY
        let low = 0;
        let high = this.cumulativeHeights.length - 1;

        while (low < high) {
            const mid = Math.floor((low + high) / 2);
            if (this.cumulativeHeights[mid] <= scrollY) {
                low = mid + 1;
            } else {
                high = mid;
            }
        }

        return Math.max(0, low - 1);
    }

    private getVisibleRange(): { startRow: number; endRow: number } | null {
        const scrollTop = this.scrollHost.scrollTop;
        const clientHeight = this.scrollHost.clientHeight;

        // If container isn't laid out yet, wait for resize observer
        if (clientHeight === 0) {
            return null;
        }

        const bufferPx = this.bufferRows * this.estimatedRowHeight;

        const viewportTop = Math.max(scrollTop - bufferPx, 0);
        const viewportBottom = scrollTop + clientHeight + bufferPx;

        const startRow = this.findRowAtPosition(viewportTop);
        const endRow = this.findRowAtPosition(viewportBottom);

        return { startRow, endRow };
    }

    private getTotalEstimatedHeight(): number {
        this.rebuildCumulativeHeights();
        return this.cumulativeHeights[this.cumulativeHeights.length - 1] || 0;
    }

    // ===== Private: Column Count Changes =====

    private getColumnsPerRow(): number {
        return this.config.getColumnsPerRow?.() ?? 1;
    }

    private handleColumnCountChange(newColumnsPerRow: number): void {
        const oldColumnsPerRow = this.previousColumnsPerRow;
        if (oldColumnsPerRow === newColumnsPerRow) return;

        // Calculate which item is at the top of viewport
        const scrollTop = this.scrollHost.scrollTop;
        const oldTopRow = this.findRowAtPosition(scrollTop);
        const topItemIndex = oldTopRow * oldColumnsPerRow;

        // Attempt to remap row heights where possible
        const newRowHeights = this.remapRowHeights(oldColumnsPerRow, newColumnsPerRow);
        this.rowHeights = newRowHeights;

        this.cumulativeHeightsDirty = true;
        this.previousColumnsPerRow = newColumnsPerRow;
        this.columnsPerRow = newColumnsPerRow;

        // Schedule render with new column count, then adjust scroll position
        this.scheduleRender();

        // After render, scroll to keep the same item visible
        requestAnimationFrame(() => {
            const newTopRow = Math.floor(topItemIndex / newColumnsPerRow);
            this.rebuildCumulativeHeights();
            const newScrollTop = this.cumulativeHeights[newTopRow] || 0;
            this.scrollHost.scrollTop = newScrollTop;
        });
    }

    private remapRowHeights(
        oldColumns: number,
        newColumns: number
    ): Map<number, number> {
        const newHeights = new Map<number, number>();

        // If heights are uniform or we can't meaningfully remap, start fresh
        if (this.rowHeights.size === 0) return newHeights;

        // For each old row, find which new row(s) its items belong to
        // Use the max height of contributing old rows for each new row
        for (const [oldRow, height] of this.rowHeights) {
            const firstItem = oldRow * oldColumns;
            const lastItem = Math.min(firstItem + oldColumns - 1, this.totalCount - 1);

            // Find new rows that contain these items
            const newStartRow = Math.floor(firstItem / newColumns);
            const newEndRow = Math.floor(lastItem / newColumns);

            for (let newRow = newStartRow; newRow <= newEndRow; newRow++) {
                const existing = newHeights.get(newRow) ?? 0;
                // Use max height as conservative estimate
                newHeights.set(newRow, Math.max(existing, height));
            }
        }

        return newHeights;
    }

    // ===== Private: Render Loop =====

    private scheduleRender(): void {
        if (this.renderScheduled) return;

        this.renderScheduled = true;
        requestAnimationFrame(() => {
            this.renderScheduled = false;
            this.render();
        });
    }

    private render(): void {
        const columnsPerRow = this.getColumnsPerRow();

        // Handle column count change (e.g., window resize)
        if (this.previousColumnsPerRow !== columnsPerRow) {
            this.handleColumnCountChange(columnsPerRow);
            return; // handleColumnCountChange schedules another render
        }

        const visibleRange = this.getVisibleRange();
        // If container isn't laid out yet, skip render - resize observer will trigger later
        if (!visibleRange) {
            return;
        }
        const { startRow, endRow } = visibleRange;

        // Convert rows to item indices
        const startIndex = startRow * columnsPerRow;
        const endIndex = Math.min((endRow + 1) * columnsPerRow - 1, this.totalCount - 1);

        // Calculate block position
        this.rebuildCumulativeHeights();
        const renderedBlockTop = this.cumulativeHeights[startRow] || 0;

        // Check if range changed
        const newRange: IndexRange = { start: startIndex, end: endIndex };
        const rangeChanged = !this.rangesEqual(newRange, this.renderedRange);

        if (rangeChanged) {
            this.renderedRange = newRange;

            // Call render callback - renderer will populate renderedBlock
            this.safeRender(this.renderedBlock, startIndex, endIndex);

            // Restore focus if needed
            this.restoreFocusIfNeeded();

            // Notify for data prefetching
            this.config.onRangeChange?.(startIndex, endIndex);
        }

        // Update container positioning
        const estimatedHeight = this.getTotalEstimatedHeight();
        this.scrollablePart.style.height = `${estimatedHeight}px`;
        this.renderedBlock.style.transform = `translateY(${renderedBlockTop}px)`;
        this.renderedBlockTop = renderedBlockTop;
    }

    private safeRender(container: HTMLElement, start: number, end: number): void {
        // Snapshot current content in case we need to restore
        const previousContent = container.innerHTML;
        const previousChildCount = container.childElementCount;

        try {
            this.config.onRender(container, start, end);
        } catch (error) {
            console.error('VirtualScroller render error:', error);

            // If render partially completed, it may have corrupted the DOM
            // Only restore if the container was cleared but not repopulated
            if (container.childElementCount === 0 && previousChildCount > 0) {
                container.innerHTML = previousContent;
            }

            // Add error indicator without disrupting layout
            const existingError = container.querySelector('.cs-virtual-scroller__error');
            if (!existingError) {
                const errorDiv = document.createElement('div');
                errorDiv.className = 'cs-virtual-scroller__error';
                errorDiv.textContent = 'Error loading items';
                errorDiv.setAttribute('role', 'alert');
                container.insertBefore(errorDiv, container.firstChild);
            }
        }
    }

    private rangesEqual(a: IndexRange | null, b: IndexRange | null): boolean {
        if (a === null || b === null) return a === b;
        return a.start === b.start && a.end === b.end;
    }

    // ===== Private: Scroll Position Preservation =====

    private setupResizeObserver(): void {
        this.itemResizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                const element = entry.target as HTMLElement;
                const visualRow = parseInt(element.dataset.virtualRow ?? '-1', 10);

                if (visualRow >= 0) {
                    const newHeight = entry.borderBoxSize?.[0]?.blockSize ?? element.offsetHeight;
                    const oldHeight = this.rowHeights.get(visualRow) ?? this.estimatedRowHeight;

                    if (Math.abs(newHeight - oldHeight) > 0.5) { // Ignore sub-pixel changes
                        this.pendingHeightChanges.set(visualRow, { oldHeight, newHeight });
                    }
                }
            }

            // Batch flush in next frame
            if (this.pendingHeightChanges.size > 0 && !this.flushHeightChangesScheduled) {
                this.flushHeightChangesScheduled = true;
                requestAnimationFrame(() => this.flushHeightChanges());
            }
        });

        this.disposables.add(() => this.itemResizeObserver.disconnect());
    }

    private flushHeightChanges(): void {
        this.flushHeightChangesScheduled = false;

        if (this.pendingHeightChanges.size === 0) return;

        const firstVisibleRow = this.findRowAtPosition(this.scrollHost.scrollTop);
        let totalScrollAdjustment = 0;

        for (const [visualRow, { oldHeight, newHeight }] of this.pendingHeightChanges) {
            this.rowHeights.set(visualRow, newHeight);

            // Only adjust scroll for rows above viewport
            if (visualRow < firstVisibleRow) {
                totalScrollAdjustment += (newHeight - oldHeight);
            }
        }

        this.pendingHeightChanges.clear();
        this.cumulativeHeightsDirty = true;

        // Single scroll adjustment for all height changes
        if (totalScrollAdjustment !== 0) {
            this.scrollHost.scrollTop += totalScrollAdjustment;
        }

        this.scheduleRender();
    }

    private shiftRowHeights(fromRow: number, rowDelta: number): void {
        if (rowDelta === 0) return;

        const newRowHeights = new Map<number, number>();

        for (const [row, height] of this.rowHeights) {
            if (row < fromRow) {
                newRowHeights.set(row, height);
            } else {
                const newRow = row + rowDelta;
                if (newRow >= 0) {
                    newRowHeights.set(newRow, height);
                }
            }
        }

        this.rowHeights = newRowHeights;
    }

    // ===== Private: Scroll Handler =====

    private setupScrollHandler(): void {
        const handleScroll = () => {
            this.scheduleRender();
        };

        this.scrollHost.addEventListener('scroll', handleScroll, { passive: true });
        this.disposables.add(() => {
            this.scrollHost.removeEventListener('scroll', handleScroll);
        });
    }

    // ===== Private: Container Resize =====

    private setupContainerResizeObserver(): void {
        const containerResizeObserver = new ResizeObserver(() => {
            this.scheduleRender();
        });

        // Observe scrollHost since that determines visible area (clientHeight)
        containerResizeObserver.observe(this.scrollHost);
        this.disposables.add(() => containerResizeObserver.disconnect());
    }

    // ===== Private: Focus Management =====

    private scheduleAnnouncement(): void {
        if (this.announcementScheduled) return;
        this.announcementScheduled = true;

        // Delay announcement to avoid noise during rapid arrow key navigation
        setTimeout(() => {
            this.announcementScheduled = false;
            this.announcePosition();
        }, 150);
    }

    private updateRovingTabindex(): void {
        const items = this.renderedBlock.querySelectorAll('[data-virtual-index]');

        Array.from(items).forEach(item => {
            const index = parseInt((item as HTMLElement).dataset.virtualIndex ?? '-1', 10);
            (item as HTMLElement).tabIndex = index === this.focusedIndex ? 0 : -1;
        });
    }

    private announcePosition(): void {
        const position = this.focusedIndex + 1;
        const total = this.totalCount;

        const message = this.config.announcePosition
            ? this.config.announcePosition(position, total)
            : `Item ${position} of ${total}`;

        this.liveRegion.textContent = message;
    }

    private restoreFocusIfNeeded(): void {
        // Only restore if scroller or its contents had focus
        if (!this.container.contains(document.activeElement)) return;

        const focusedEl = this.renderedBlock.querySelector(
            `[data-virtual-index="${this.focusedIndex}"]`
        ) as HTMLElement | null;

        if (focusedEl) {
            focusedEl.focus({ preventScroll: true });
        }
    }

    private isInRenderedRange(index: number): boolean {
        if (!this.renderedRange) return false;
        return index >= this.renderedRange.start && index <= this.renderedRange.end;
    }

    // ===== Private: Interval Operations =====

    private mergeInterval(intervals: IndexRange[], newInterval: IndexRange): IndexRange[] {
        const result: IndexRange[] = [];
        let merged = { ...newInterval };
        let inserted = false;

        for (const interval of intervals) {
            if (interval.end < merged.start - 1) {
                // Interval is completely before merged
                result.push(interval);
            } else if (interval.start > merged.end + 1) {
                // Interval is completely after merged
                if (!inserted) {
                    result.push(merged);
                    inserted = true;
                }
                result.push(interval);
            } else {
                // Overlapping or adjacent - merge
                merged = {
                    start: Math.min(merged.start, interval.start),
                    end: Math.max(merged.end, interval.end)
                };
            }
        }

        if (!inserted) {
            result.push(merged);
        }

        return result;
    }

    private removeInterval(intervals: IndexRange[], toRemove: IndexRange): IndexRange[] {
        const result: IndexRange[] = [];

        for (const interval of intervals) {
            if (interval.end < toRemove.start || interval.start > toRemove.end) {
                // No overlap
                result.push(interval);
            } else {
                // Overlapping - may need to split
                if (interval.start < toRemove.start) {
                    result.push({ start: interval.start, end: toRemove.start - 1 });
                }
                if (interval.end > toRemove.end) {
                    result.push({ start: toRemove.end + 1, end: interval.end });
                }
            }
        }

        return result;
    }
}
